import { Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { DataTableColumn, DataTableComponent } from '../../shared/data-table/data-table.component';
import { Carrier } from '../../user-dashboard/shared/carrier.model';
import { WebhookEventService } from '../shared/webhook-event.service';
import { SortDirection } from '../../shared/sort-direction';
import { WebhookEventSearchCriteria, WebhookEventSortableColumn } from '../shared/webhook-event-search-criteria.model';
import { Moment } from 'moment';
import { allEventStatus, EventStatus, WebhookEvent, WebhookEventSearchResult } from '../shared/webhook-event.model';
import { allEventTypes } from '../shared/webhook-subscription.model';
import { TranslateService } from '@ngx-translate/core';
import { Option } from '../../shared/data-filters/data-filter-autocomplete/data-filter-autocomplete.component';
import * as moment from 'moment-timezone';
import { finalize } from 'rxjs/operators';
import { LoaderService } from '../../shared/services/loader.service';
import { ToastrService } from 'ngx-toastr';
import { ErrorHandlerService } from '../../shared/services/error-handler.service';
import { SearchObjectResult } from '../../shared/search-object-result.model';
import { ModalService } from '../../shared/services/modal.service';
import { NavigationLinkService } from '../../shared/services/navigation-link.service';

@Component({
  selector: 'app-webhook-events-list',
  templateUrl: './webhook-events-list.component.html',
  styleUrls: ['./webhook-events-list.component.scss']
})
export class WebhookEventsListComponent implements OnInit {

  public triggeredStartDateMoment: Moment;
  public triggeredEndDateMoment: Moment;
  public selectedEventStatuses: Option[];
  public allEventStatuses: Option[];
  public selectedEventTypes: Option[];
  public allEventTypes: Option[];

  public filters: WebhookEventSearchCriteria;
  public columns: DataTableColumn[] = [];
  public sortColumn: DataTableColumn;
  public direction = SortDirection.DESC;

  events: WebhookEvent[] = [];
  nbSelectedEvents = 0;

  @Output()
  private onEventSelection = new EventEmitter<WebhookEventSearchResult>();

  private _carrier: Carrier;

  @Input()
  get carrier() {
    return this._carrier;
  }

  set carrier(carrier: Carrier) {
    this._carrier = carrier;
    if (carrier) {
      this.filters = {
        ... this.filters,
        timeZone: moment.tz.guess(),
        subscriberId: carrier.id
      };
      this.dataTableComponent.changePage(1, true);
    }
  }

  @ViewChild('dataTable', { static: true})
  private dataTableComponent: DataTableComponent;

  @ViewChild('dateColumn', { static: true})
  private dateColumn: TemplateRef<any>;

  @ViewChild('deliveryColumn', { static: true})
  private deliveryColumn: TemplateRef<any>;

  @ViewChild('eventTypeColumn', { static: true})
  private eventTypeColumn: TemplateRef<any>;

  @ViewChild('statusColumn', { static: true})
  private statusColumn: TemplateRef<any>;

  @ViewChild('actionsColumn', { static: true})
  private actionsColumn: TemplateRef<any>;

  loadPage = (page: number, pageSize: number, sortColumn: DataTableColumn, direction: SortDirection) =>
    this.webhookEventService.searchWebhookEvents(page - 1, pageSize, sortColumn.name, direction, this.filters);

  constructor(
    private webhookEventService: WebhookEventService,
    private translateService: TranslateService,
    private loader: LoaderService,
    private toastr: ToastrService,
    private errorHandler: ErrorHandlerService,
    private translate: TranslateService,
    private modal: ModalService,
    public link: NavigationLinkService,
    ) {
  }

  ngOnInit() {

    this.allEventStatuses = allEventStatus().map(status => ({
      value: status,
      label: this.translateService.instant('webhooks-events.list.filters.event-status.' + status.toString())
    }));

    this.allEventTypes = allEventTypes().map(type => ({
      value: type,
      label: this.translateService.instant('webhooks-events.list.filters.event-type.' + type.toString())
    }));

    const dateColumn = {
      name: WebhookEventSortableColumn.TRIGGERED_DATE,
      displayed: true,
      translationKey: 'webhooks-events.list.data.date',
      template: this.dateColumn,
      sortable: true
    };

    this.columns = [
      dateColumn,
      {
        name: 'DELIVERY',
        displayed: true,
        translationKey: 'webhooks-events.list.data.delivery',
        template: this.deliveryColumn,
        sortable: false,
      },
      {
        name: WebhookEventSortableColumn.EVENT_TYPE,
        displayed: true,
        translationKey: 'webhooks-events.list.data.event-type',
        template: this.eventTypeColumn,
        sortable: true
      },
      {
        name: WebhookEventSortableColumn.EVENT_STATUS,
        displayed: true,
        translationKey: 'webhooks-events.list.data.status',
        template: this.statusColumn,
        sortable: true
      },
      {
        name: 'actions',
        displayed: true,
        translationKey: 'webhooks-events.list.data.actions',
        template: this.actionsColumn,
        sortable: false
      }
    ];

    this.sortColumn = dateColumn;
  }

  public onTriggeredStartDateChange(date: Moment): void {
    this.triggeredStartDateMoment = date;
    this.filters.triggeredStartDate = date ? date.utc(true).valueOf() : undefined;
  }

  public onTriggeredEndDateChange(date: Moment): void {
    this.triggeredEndDateMoment = date;
    this.filters.triggeredEndDate = date ? date.utc(true).valueOf() : undefined;
  }

  public onSelectedEventTypesChange(selectedTypeOptions: Option[]): void {
    this.selectedEventTypes = selectedTypeOptions;
    this.filters.eventTypes = selectedTypeOptions ? selectedTypeOptions.map(typeOption => typeOption.value) : undefined;
  }

  public onSelectedEventStatusesChange(selectedStatusOptions: Option[]): void {
    this.selectedEventStatuses = selectedStatusOptions;
    this.filters.eventStatuses = selectedStatusOptions ? selectedStatusOptions.map(statusOption => statusOption.value) : undefined;
  }

  public onReset(): void {
    this.triggeredEndDateMoment = undefined;
    this.triggeredStartDateMoment = undefined;
    this.selectedEventStatuses = [];
    this.selectedEventTypes = [];
    this.filters = {
      timeZone: moment.tz.guess(),
      subscriberId: this.carrier.id
    };
  }

  public onEventSelected(selectedEvent: WebhookEventSearchResult) {
    this.onEventSelection.next(selectedEvent);
  }

  onLoad(result: SearchObjectResult<WebhookEvent>) {
    this.events = result.results;
  }

  openBulkRetrySendingEventsConfirm(modal: any) {
    this.nbSelectedEvents = this.events.filter(e => e.selected).length;

    if (this.nbSelectedEvents === 0) {
      return;
    }

    this.modal.open({
      title: this.translate.instant('webhooks-events.list.data.bulk-retry-send.title'),
      content: modal,
      closeButton: {
        text: this.translate.instant('buttons.no')
      },
      validateButton: {
        type: 'primary',
        text: this.translate.instant('buttons.yes'),
      },
      size: 'sm'
    }).subscribe(() => this.bulkRetrySendingEvents());
  }

  bulkRetrySendingEvents(): void {
    const eventIds = this.events.filter(e => e.selected)
      .map(e => e.id);

    this.loader.startLoad();
    this.webhookEventService.bulkRetrySendingEvents(eventIds)
      .pipe(finalize(() => this.loader.stopLoad()))
      .subscribe(events => {
        events.forEach(e1 => this.events.filter(e2 => e1.id === e2.id).forEach(event => {
          event.status = e1.status;
          if (event.status !== EventStatus.FAILED) {
            event.selected = false;
          }
        }));
        this.toastr.success(this.translate.instant('webhooks-events.list.data.bulk-retry-send.success'))
      }, e => this.errorHandler.handleHttpError(e, 'webhooks-events.list.data.bulk-retry-send.error'));
  }

  selectAll() {
    this.events.filter(e => e.status === EventStatus.FAILED)
      .forEach(e => e.selected = true);
  }
}
